import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import { IdTypes, createFeatureScope } from 'bundles/product-features';
import {
  ORG_SCOPED_HOME_OVERRIDE_ORG_IDS,
  ORG_SCOPED_HOME_STATSIG_GATE,
} from 'bundles/unified-common/constants/orgScopedHomeStatsig';

const { useProductFeatures } = /* @__PURE__ */ createFeatureScope({
  enterprise: {
    enableOrgScopedHome: false,
  },
});

export type AccountSettingsContextSwitcherGateValue = {
  /**
   * True when the Statsig `org-scoped-home` gate passes (stable-ID rollout), the learner has
   * exactly one enterprise org (from program enrollments / page header), and that org is in the
   * org-scoped home override set or has the `enableOrgScopedHome` product feature enabled.
   *
   * Intentionally does **not** use `useIsOrgScopedHome`: that hook reflects the **current** UX
   * selection (org vs personal). After switching to personal, org-scoped product data is empty and
   * the switcher would incorrectly hide — but the user still needs Switch Account to return to org.
   */
  shouldRenderContextSwitcher: boolean;
};

const defaultValue: AccountSettingsContextSwitcherGateValue = {
  shouldRenderContextSwitcher: false,
};

const AccountSettingsContextSwitcherGateContext =
  React.createContext<AccountSettingsContextSwitcherGateValue>(defaultValue);

/**
 * Must render inside `ConsumerMyPageHeaderContext` and `StatsigProvider`.
 * Logs Statsig exposure for impression tracking when the switcher is active.
 */
export function AccountSettingsContextSwitcherGateProvider({ children }: { children: React.ReactNode }) {
  // @ts-expect-error FIXME: please avoid using the `disableExposureLog` and use qualifying events to filter exposures
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();
  const { naptimeOrganizationsWithProgramEnrollments } = useConsumerMyPageHeader();
  const orgs = naptimeOrganizationsWithProgramEnrollments ?? [];
  const isExactlyOneEnterpriseOrg = orgs.length === 1;
  const singleOrg = isExactlyOneEnterpriseOrg ? orgs[0] : undefined;
  const orgId = singleOrg?.id ?? '';

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: orgId,
    skip: !orgId,
  });
  const featureEnabled = orgId ? features.get('enterprise', 'enableOrgScopedHome') : false;

  const shouldRenderContextSwitcher =
    userInOrgScopedRollout &&
    isExactlyOneEnterpriseOrg &&
    !!orgId &&
    (ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(orgId) || featureEnabled);

  useEffect(() => {
    if (shouldRenderContextSwitcher) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [shouldRenderContextSwitcher, checkGate]);

  const value = useMemo(() => ({ shouldRenderContextSwitcher }), [shouldRenderContextSwitcher]);

  return (
    <AccountSettingsContextSwitcherGateContext.Provider value={value}>
      {children}
    </AccountSettingsContextSwitcherGateContext.Provider>
  );
}

export function useAccountSettingsContextSwitcherGate(): AccountSettingsContextSwitcherGateValue {
  return React.useContext(AccountSettingsContextSwitcherGateContext);
}
